import React, {useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Check, AlertCircle } from 'lucide-react';
import { validators, formatters } from '../../utils/validators';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';
import { isApiConfigured } from '../../api/axiosClient';
import { userApi } from '../../api/userApi';

/**
 * Provider Payment Enrollment — EFT (direct deposit) or ERA (835 remittance).
 * ==========================================================================
 * Two separate full-width pages, styled with the portal's own tokens/classes
 * (.page-header, .card, .btn-primary, CSS variables). `kind` selects the form:
 *   /forms/eft-enrollment → kind="eft"   ·   /forms/era-enrollment → kind="era"
 * Field sets follow CAQH CORE 380 (EFT) / 382 (ERA). Provider fields prefill
 * from the signed-in user. Read-only mock submit → "received" confirmation.
 *
 * Validation matches the rest of the portal (ValidatedInput / RegisterPage):
 * every required field shows a red border + "<Field> is required" (or a
 * format message) once it is touched or after a submit attempt, and Submit
 * shows "Please fill in all required fields." and scrolls to the first error.
 */

const REASONS = ['New enrollment', 'Change', 'Cancel'];

/* ABA routing-number checksum (9 digits, weights 3-7-1). */
const abaRouting = (v) => {
  if (!v) return null;
  const d = String(v).replace(/\D/g, '');
  if (d.length !== 9) return 'Routing number must be 9 digits';
  const sum = [3, 7, 1, 3, 7, 1, 3, 7, 1].reduce((s, w, i) => s + w * Number(d[i]), 0);
  return sum % 10 === 0 ? null : 'Routing number failed the ABA checksum — please re-check it';
};
const accountNumber = (v) => (!v ? null : /^\d{4,17}$/.test(String(v).replace(/\s/g, '')) ? null : 'Account number must be 4–17 digits');
const notInPast = (v) => {
  if (!v) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return new Date(v + 'T00:00:00') < today ? 'Date cannot be in the past' : null;
};

/* Compact marker next to a label: * = required, ▲ = conditional, none = optional. */
const Tag = ({ kind }) => {
  if (kind === 'req') return <span className="enroll-req" title="Required" aria-label="required">*</span>;
  if (kind === 'cond') return <span className="enroll-cond" title="Conditional" aria-label="conditional">▲</span>;
  return null; // optional fields carry no marker
};

/* One labelled field spanning `c` of 12 columns, with the portal's error style. */
function F({ label, tag, c = 4, hint, error, children, field }) {
  return (
    <div className={`enroll-fg${error ? ' has-error' : ''}`} style={{ gridColumn: `span ${c}` }} data-field={field}>
      <label>{label}{tag && <Tag kind={tag} />}</label>
      {children}
      {error ? (
        <span className="enroll-error" role="alert"><AlertCircle size={13} strokeWidth={2.2} /> {error}</span>
      ) : (hint && <span className="enroll-hint">{hint}</span>)}
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div className="card">
      <div className="enroll-sec-head">
        <h3>{title}</h3>
        {note && <span>{note}</span>}
      </div>
      <div className="enroll-grid">{children}</div>
    </div>
  );
}

export default function EnrollmentPage({ kind = 'eft' }) {
    const { user } = useAuth();
    const [userTaxIds, setUserTaxIds] = useState([]);
    const [selectedTaxId, setSelectedTaxId] = useState('');
    const [pageLoading, setPageLoading] = useState(false);
  const isEft = kind === 'eft';

  // Prefill from the signed-in provider.
  const org = user?.orgName || user?.organizationName || '';
  const npi = user?.npi || '';
  const tin = user?.taxId || '';
  const contact = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const email = user?.email || '';
  //const linkOptions = useMemo(() => [npi ? `NPI — ${npi}` : 'NPI', tin ? `TIN — ${tin}` : 'TIN'], [npi, tin]);
    const linkOptions = useMemo(() => [npi ? `NPI — ${npi}` : 'NPI', tin ? `TIN — ${selectedTaxId}` : 'TIN'], [npi, tin, selectedTaxId]);

  const [reason, setReason] = useState('New enrollment');
  const [agentOn, setAgentOn] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');
    const counter = useRef(122);


    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        gridOverlay: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
        }
    };

  // All form values (controlled). Keys double as field ids for validation.
  const [f, setF] = useState(() => ({
    legalName: org, dba: '', tin, npi, contact, email,
    tradingPartnerId: '', phone: user?.phone || '', fax: '',
    address: user?.address || '', city: user?.city || '', state: user?.state || '', zip: user?.zip || '',
    agentName: '', agentPhone: '', agentEmail: '',
    bankName: '', routing: '', accountType: 'Checking', account: '', linkTo: linkOptions[0], bankPhone: '',
    aggregateBy: 'Provider Tax ID (TIN)', associateWith: linkOptions[0], eraMethod: 'Download from portal',
    chName: '', chId: '',
    effectiveDate: '', signer: contact, title: '', signature: '', signatureDate: '',
  }));
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const touch = (k) => () => setTouched((t) => ({ ...t, [k]: true }));
  const bind = (k, fmt) => ({
    value: f[k] ?? '',
    onChange: (e) => set(k)(fmt ? fmt(e.target.value) : e.target.value),
    onBlur: touch(k),
  });

  /* Rules for the fields visible on THIS form. Each: [key, label, validateFn?] */
  const rules = useMemo(() => {
    const r = [
      ['legalName', 'Provider (legal) name'],
      ['tin', 'Federal Tax ID', validators.taxId],
      ['npi', 'NPI', validators.npi],
      ['contact', 'Contact name'],
      ['email', 'Email', validators.email],
    ];
    if (isEft) {
      r.push(['phone', 'Telephone', validators.phone], ['address', 'Street address'], ['city', 'City'],
        ['state', 'State', validators.state], ['zip', 'ZIP', validators.zip]);
      if (agentOn) r.push(['agentName', 'Agent name'], ['agentPhone', 'Agent phone', validators.phone], ['agentEmail', 'Agent email', validators.email]);
      r.push(['bankName', 'Bank name'], ['routing', 'Routing number', abaRouting], ['accountType', 'Account type'],
        ['account', 'Account number', accountNumber], ['linkTo', 'Link account to identifier']);
    } else {
      r.push(['aggregateBy', 'Aggregate the 835 by'], ['associateWith', 'Associate ERA with'], ['eraMethod', 'Retrieval method']);
      if (/clearinghouse/i.test(f.eraMethod)) r.push(['chName', 'Clearinghouse / vendor name'], ['chId', 'Clearinghouse / vendor ID']);
    }
    r.push(['effectiveDate', 'Requested effective date', notInPast], ['signer', 'Authorized signer'], ['title', 'Title'],
      ['signature', 'Electronic signature'], ['signatureDate', 'Signature date']);
    return r;
  }, [isEft, agentOn, f.eraMethod]);

  // Optional-but-validated fields (format only, when filled).
  const optionalRules = useMemo(() => (isEft
    ? [['fax', validators.phone], ['bankPhone', validators.phone]]
    : []), [isEft]);

  const errorFor = useCallback((k) => {
    const rule = rules.find(([key]) => key === k);
    if (rule) {
      const [, label, fn] = rule;
      return validators.required(f[k], label) || (fn ? fn(f[k]) : null);
    }
    const opt = optionalRules.find(([key]) => key === k);
    return opt ? opt[1](f[k]) : null;
  }, [rules, optionalRules, f]);

  // Show a field's error only once it is touched or the user tried to submit.
  const err = (k) => (touched[k] || submitted) ? errorFor(k) : null;

  const submit = () => {
    setSubmitted(true);
    const firstBad = [...rules, ...optionalRules].map(([k]) => k).find((k) => errorFor(k));
    if (firstBad) {
      setError('Please fill in all required fields.');
      // Scroll to + focus the first invalid field (fields carry data-field=<key>).
      const el = document.querySelector(`.enroll [data-field="${firstBad}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.querySelector('input,select')?.focus({ preventScroll: true });
      return;
    }
    if (!agree) { setError('Please check the attestation before submitting.'); return; }
    setError('');
    counter.current += 1;
    const conf = `ENR-2026-000${counter.current}`;
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
    const providerLine = `Provider: ${f.legalName || 'Your organization'}  (NPI ${f.npi || '—'}, TIN ${f.tin || '—'})`;
    setReceipt(isEft
      ? `Confirmation: ${conf}    Submitted: ${now}\nReason: ${reason}\n${providerLine}\nBank details: account number masked on screen.\n\nStatus: Received — pending validation. A confirmation will be emailed to ${f.email || 'your billing contact'}.`
      : `Confirmation: ${conf}    Submitted: ${now}\nReason: ${reason}\n${providerLine}\nRetrieval: ${f.eraMethod}\n\nStatus: Received — pending validation. A confirmation will be emailed to ${f.email || 'your billing contact'}.`);
  };

    useEffect(() => {
        if (user?.userName) {

            getUserTaxIds(user?.userName);
        }
    }, [user?.userName]);


    ////onchange
    //const handleTaxIdChange = (e) => {
    //    const taxId = e.target.value;

    //    setSelectedTaxId(taxId);

    //    // also need it in our form:
    //    bind('tin').onChange(e);
    //};

    //get user taxIds 
    const getUserTaxIds = async (userName) => {

        if (isApiConfigured()) {
            setPageLoading(true);

            try {
                var res = await userApi.getUsersTaxIds({
                    userName: userName
                });


                if (res.success) {
                    setUserTaxIds(res?.data?.TaxIds);
                    console.log("UserTaxIds get successfully");
                }
                else {
                    setUserTaxIds([]);
                    console.log(res.message);
                }
                setPageLoading(false);

            } catch (err) {
                console.log("Error at getUserTaxIds : " + err);
                setPageLoading(false);

            } finally {

            }
        }
    };

  return (
      <div className="enroll">
          {pageLoading && (
              <div style={styles.overlay}>
                  <LoadingSpinner data-cy="page-loader-profile" message="Please wait..." fullPage="true" />
              </div>
          )}
      <div className="page-header">
        <div>
          <h1>{isEft ? 'EFT Enrollment' : 'ERA Enrollment'}</h1>
          <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
            {isEft ? 'Electronic Funds Transfer — receive claim payments by ACH direct deposit.'
                   : 'Electronic Remittance Advice — receive the electronic 835 explanation of payment.'}
          </p>
        </div>
      </div>

      {/* Reason for submission */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>Reason for submission</div>
        <div className="enroll-seg">
          {REASONS.map((r) => (
            <button key={r} type="button" className={r === reason ? 'on' : ''} onClick={() => setReason(r)}>{r}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <span className="enroll-legend">
          <span><b style={{ color: 'var(--status-terminated,#DC2626)' }}>*</b> Required</span>
          <span><b style={{ color: 'var(--status-pending,#B45309)' }}>▲</b> Conditional</span>
          <span style={{ color: 'var(--text-tertiary,#9CA3AF)' }}>Unmarked = optional</span>
        </span>
      </div>

      {/* ---- Provider Information (both) ---- */}
      <Section title="1 · Provider Information" note={isEft ? 'Who is being paid' : undefined}>
        <F label="Provider (legal) name" tag="req" c={4} error={err('legalName')} field="legalName"><input {...bind('legalName')} placeholder="Legal name of the practice" /></F>
        <F label="Doing-business-as (DBA)" tag="opt" c={4}><input {...bind('dba')} placeholder="DBA name, if any" /></F>
              <F label="Federal Tax ID (TIN/EIN)" tag="req" c={4} hint="9 digits" error={err('tin')} field="tin">
                  {user.userRole.trim().toLowerCase() === 'super admin' ? (
                      <input inputMode="numeric" {...bind('tin', formatters.taxId)} placeholder="94-1234567" />
                  ) : (
                          <select
                              /*{...bind('tin')}*/
                              value={selectedTaxId}
                              onChange={(e) => setSelectedTaxId(e.target.value)}
                          >
                              <option value="">Select Federal Tax ID</option>

                              {userTaxIds?.map((taxId) => (
                                  <option key={taxId} value={taxId}>
                                      {taxId.length === 9
                                          ? `${taxId.slice(0, 2)}-${taxId.slice(2)}`
                                          : taxId}
                                  </option>
                              ))}
                          </select>
                  )}
                  
                  

              </F>
        <F label="NPI" tag="req" c={3} hint="10 digits" error={err('npi')} field="npi"><input inputMode="numeric" {...bind('npi', formatters.numeric)} maxLength={10} placeholder="1234567893" /></F>
        <F label="Contact name" tag="req" c={5} error={err('contact')} field="contact"><input {...bind('contact')} placeholder="A. Morales, Billing Manager" /></F>
        <F label="Email" tag="req" c={4} error={err('email')} field="email"><input type="email" {...bind('email')} placeholder="billing@clinic.example" /></F>
        {isEft && <>
          <F label="Trading Partner ID" tag="opt" c={3}><input {...bind('tradingPartnerId')} placeholder="4137147" /></F>
          <F label="Telephone" tag="req" c={3} error={err('phone')} field="phone"><input inputMode="tel" {...bind('phone', formatters.phone)} placeholder="(555) 010-2233" /></F>
          <F label="Fax" tag="opt" c={3} error={err('fax')} field="fax"><input inputMode="tel" {...bind('fax', formatters.phone)} placeholder="(555) 010-2234" /></F>
          <F label="Street address" tag="req" c={5} error={err('address')} field="address"><input {...bind('address')} placeholder="4080 SE Gatehouse" /></F>
          <F label="City" tag="req" c={3} error={err('city')} field="city"><input {...bind('city')} placeholder="Austin" /></F>
          <F label="State" tag="req" c={2} error={err('state')} field="state"><input {...bind('state', formatters.state)} maxLength={2} placeholder="TX" /></F>
          <F label="ZIP+4" tag="req" c={2} error={err('zip')} field="zip"><input inputMode="numeric" {...bind('zip', formatters.zip)} placeholder="78702-0000" /></F>
          <div className="enroll-subrow" style={{ gridColumn: 'span 12' }}>
            <label className="enroll-check"><input type="checkbox" checked={agentOn} onChange={(e) => setAgentOn(e.target.checked)} /> A third-party agent is submitting on the provider's behalf</label>
          </div>
          {agentOn && <>
            <F label="Agent name" tag="cond" c={6} error={err('agentName')} field="agentName"><input {...bind('agentName')} placeholder="Billing service name" /></F>
            <F label="Agent phone" tag="cond" c={3} error={err('agentPhone')} field="agentPhone"><input inputMode="tel" {...bind('agentPhone', formatters.phone)} placeholder="(555) 777-0100" /></F>
            <F label="Agent email" tag="cond" c={3} error={err('agentEmail')} field="agentEmail"><input type="email" {...bind('agentEmail')} placeholder="ops@billing.example" /></F>
          </>}
        </>}
      </Section>

      {/* ---- EFT: Financial Institution ---- */}
      {isEft && (
        <Section title="2 · Financial Institution" note="Where deposits go">
          <F label="Bank name" tag="req" c={5} error={err('bankName')} field="bankName"><input {...bind('bankName')} placeholder="First National Bank" /></F>
          <F label="Routing number (ABA)" tag="req" c={3} hint="9 digits · ABA checksum" error={err('routing')} field="routing"><input inputMode="numeric" autoComplete="off" maxLength={9} {...bind('routing', formatters.numeric)} placeholder="091000019" /></F>
          <F label="Account type" tag="req" c={4} error={err('accountType')} field="accountType"><select {...bind('accountType')}><option>Checking</option><option>Savings</option></select></F>
          <F label="Account number" tag="req" c={5} hint="Masked on screen" error={err('account')} field="account">
            <div style={{ position: 'relative' }}>
              <input type={showAcct ? 'text' : 'password'} inputMode="numeric" autoComplete="off" maxLength={17} {...bind('account', formatters.numeric)} placeholder="•••• •••• 6789" style={{ paddingRight: 60 }} />
              <button type="button" className="enroll-inbtn" onClick={() => setShowAcct((v) => !v)}>{showAcct ? 'Hide' : 'Show'}</button>
            </div>
          </F>
          <F label="Link account to identifier" tag="req" c={4} hint="CORE reassociation linkage" error={err('linkTo')} field="linkTo"><select {...bind('linkTo')}>{linkOptions.map((o) => <option key={o}>{o}</option>)}</select></F>
          <F label="Bank contact phone" tag="opt" c={3} error={err('bankPhone')} field="bankPhone"><input inputMode="tel" {...bind('bankPhone', formatters.phone)} placeholder="(800) 555-1212" /></F>
        </Section>
      )}

      {/* ---- ERA: Remittance Preferences ---- */}
      {!isEft && (
        <Section title="2 · Remittance Advice Preferences" note="CORE 382">
          <F label="Aggregate the 835 by" tag="req" c={5} hint="Grouping preference" error={err('aggregateBy')} field="aggregateBy"><select {...bind('aggregateBy')}><option>Provider Tax ID (TIN)</option><option>NPI</option></select></F>
          <F label="Associate ERA with" tag="req" c={4} error={err('associateWith')} field="associateWith"><select {...bind('associateWith')}>{linkOptions.map((o) => <option key={o}>{o}</option>)}</select></F>
          <F label="Retrieval method" tag="req" c={3} error={err('eraMethod')} field="eraMethod"><select {...bind('eraMethod')}><option>Download from portal</option><option>Via clearinghouse / vendor</option><option>Direct connection</option></select></F>
          {/clearinghouse/i.test(f.eraMethod) && <>
            <F label="Clearinghouse / vendor name" tag="cond" c={6} error={err('chName')} field="chName"><input {...bind('chName')} placeholder="Clearinghouse name" /></F>
            <F label="Clearinghouse / vendor ID" tag="cond" c={6} error={err('chId')} field="chId"><input {...bind('chId')} placeholder="Clearinghouse ID" /></F>
          </>}
        </Section>
      )}

      {/* ---- Submission & Authorization (both) ---- */}
      <Section title="3 · Submission & Authorization">
        <F label="Requested effective date" tag="req" c={3} error={err('effectiveDate')} field="effectiveDate"><input type="date" {...bind('effectiveDate')} /></F>
        <F label="Authorized signer (printed)" tag="req" c={5} error={err('signer')} field="signer"><input {...bind('signer')} placeholder="Full name of authorized signer" /></F>
        <F label="Title" tag="req" c={4} error={err('title')} field="title"><input {...bind('title')} placeholder="Practice Administrator" /></F>
        <F label="Electronic signature" tag="req" c={8} error={err('signature')} field="signature"><input {...bind('signature')} placeholder="Type full name to sign" /></F>
        <F label="Signature date" tag="req" c={4} error={err('signatureDate')} field="signatureDate"><input type="date" {...bind('signatureDate')} /></F>
      </Section>

      {/* Submit bar */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <label className={`enroll-attest${submitted && !agree ? ' has-error' : ''}`}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>I certify the information is accurate and I am authorized to enroll this provider for {isEft ? 'EFT. I authorize deposits to the account above.' : 'ERA.'}
            {submitted && !agree && <span className="enroll-error" style={{ display: 'block', marginTop: 4 }}><AlertCircle size={13} strokeWidth={2.2} /> Attestation is required</span>}
          </span>
        </label>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-primary" onClick={submit}>Submit enrollment</button>
      </div>
      {error && <div className="error-text" style={{ color: 'var(--status-terminated,#DC2626)', fontSize: 13, fontWeight: 600, marginTop: -8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={15} strokeWidth={2.2} /> {error}</div>}

      {receipt && (
        <div className="card" style={{ borderColor: 'var(--status-active,#10B981)', background: 'var(--status-active-bg,#D1FAE5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 15, color: '#047857', marginBottom: 10 }}>
            <Check size={18} strokeWidth={2.6} /> {isEft ? 'EFT' : 'ERA'} enrollment received
          </div>
          <pre style={{ margin: 0, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>{receipt}</pre>
        </div>
      )}

      {/* How it works */}
      <div className="card">
        <div className="enroll-sec-head"><h3>How enrollment works</h3></div>
        <div className="enroll-steps">
          {[
            ['Choose a reason', 'New enrollment, Change (update existing details), or Cancel.'],
            ['Enter provider info', 'Legal name, Tax ID, NPI, and a billing contact — where confirmations are sent.'],
            isEft ? ['Add banking details', 'Bank, routing, account type/number, and the NPI or TIN the account links to. The account number is masked on screen.']
                  : ['Set remittance preferences', 'How the 835 is grouped (TIN or NPI) and how you retrieve it (portal, clearinghouse, or direct).'],
            ['Sign and submit', 'An authorized person e-signs. You get a confirmation number now; a second notice follows when it is active.'],
          ].map(([h, p], i) => (
            <div key={i} className="enroll-step">
              <span className="n">{i + 1}</span>
              <div><b>{h}</b><div>{p}</div></div>
            </div>
          ))}
        </div>
        <div className="enroll-note">
          {isEft ? 'Enroll in ERA too so payments and remittances auto-reassociate.' : 'Enroll in EFT too so payments and remittances auto-reassociate.'} Need help? Reach Provider Services via Contact & Resources.
        </div>
      </div>

      <style>{`
        .enroll .enroll-grid { display:grid; grid-template-columns:repeat(12,minmax(0,1fr)); gap:16px 20px; }
        .enroll .enroll-fg { display:flex; flex-direction:column; gap:6px; min-width:0; }
        .enroll .enroll-fg > label { font-size:13px; font-weight:600; color:var(--text-secondary,#6B7280); letter-spacing:.01em; display:flex; align-items:center; gap:8px; }
        .enroll .enroll-req { color:var(--status-terminated,#DC2626); font-weight:800; font-size:15px; line-height:1; }
        .enroll .enroll-cond { color:var(--status-pending,#B45309); font-size:10px; line-height:1; }
        .enroll .enroll-hint { font-size:11.5px; color:var(--text-tertiary,#9CA3AF); font-family:ui-monospace,Menlo,monospace; }
        .enroll .enroll-error { font-size:12px; color:#EF4444; display:flex; align-items:center; gap:4px; }
        .enroll input, .enroll select { width:100%; background:var(--bg-input,#F8FAFB); border:1px solid var(--border-color,#E8ECF0); border-radius:var(--radius-sm,10px); padding:10px 12px; font:inherit; font-size:14px; color:var(--text-primary,#1A1D21); outline:none; transition:border-color .15s, background .15s; }
        .enroll input:focus, .enroll select:focus { border-color:var(--primary,#0D9488); background:#fff; }
        .enroll .has-error input, .enroll .has-error select { border-color:#EF4444; background:#FEF2F2; }
        .enroll .has-error input:focus, .enroll .has-error select:focus { border-color:#EF4444; box-shadow:0 0 0 3px rgba(239,68,68,.12); }
        .enroll input[type=checkbox] { width:auto; }
        .enroll .enroll-sec-head { display:flex; align-items:center; gap:10px; margin:0 0 18px; padding-bottom:12px; border-bottom:1px solid var(--border-color,#E8ECF0); }
        .enroll .enroll-sec-head h3 { margin:0; font-size:15px; font-weight:700; color:var(--text-primary,#1A1D21); }
        .enroll .enroll-sec-head span { margin-left:auto; font-size:12.5px; color:var(--text-tertiary,#9CA3AF); }
        .enroll .enroll-seg { display:inline-flex; background:var(--bg-subtle,#F1F5F9); border:1px solid var(--border-color,#E8ECF0); border-radius:10px; padding:3px; }
        .enroll .enroll-seg button { border:0; background:transparent; font:inherit; font-size:13px; font-weight:600; color:var(--text-secondary,#6B7280); padding:7px 15px; border-radius:7px; cursor:pointer; }
        .enroll .enroll-seg button.on { background:var(--gradient-primary,linear-gradient(135deg,#0F766E,#0D9488)); color:#fff; }
        .enroll .enroll-legend { display:flex; gap:14px; font-size:12px; font-weight:600; }
        .enroll .enroll-subrow { border-top:1px dashed var(--border-color,#E8ECF0); padding-top:14px; }
        .enroll .enroll-check, .enroll .enroll-attest { display:flex; align-items:center; gap:9px; font-size:13.5px; color:var(--text-primary,#1A1D21); cursor:pointer; }
        .enroll .enroll-attest { align-items:flex-start; max-width:620px; color:var(--text-secondary,#6B7280); }
        .enroll .enroll-attest.has-error { color:#B91C1C; }
        .enroll .enroll-inbtn { position:absolute; right:6px; top:50%; transform:translateY(-50%); border:0; background:var(--bg-subtle,#F1F5F9); color:var(--text-secondary,#6B7280); font:inherit; font-size:12px; font-weight:600; padding:5px 10px; border-radius:6px; cursor:pointer; }
        .enroll .enroll-steps { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:16px; }
        .enroll .enroll-step { display:flex; gap:11px; align-items:flex-start; }
        .enroll .enroll-step .n { flex:none; width:24px; height:24px; border-radius:7px; background:rgba(13,148,136,.1); color:var(--primary-dark,#0F766E); display:grid; place-items:center; font-size:12.5px; font-weight:800; }
        .enroll .enroll-step b { font-size:13.5px; color:var(--text-primary,#1A1D21); }
        .enroll .enroll-step div div { font-size:12.5px; color:var(--text-secondary,#6B7280); margin-top:2px; line-height:1.5; }
        .enroll .enroll-note { margin-top:16px; padding:11px 15px; border-left:3px solid var(--primary,#0D9488); background:var(--bg-subtle,#F1F5F9); border-radius:0 8px 8px 0; font-size:13px; color:var(--text-secondary,#6B7280); }
        @media (max-width:860px){
          .enroll .enroll-grid { grid-template-columns:repeat(2,1fr); }
          .enroll .enroll-grid > * { grid-column:span 2 !important; }
        }
      `}</style>
    </div>
  );
}
